import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BookingGroupExcludedElement } from '../../model/booking-group-excluded-element.model';
import { Element } from '../../model/element.model';

interface BookingExcludedElementApiModel {
  booking_ExcludedElement_ID: string;
  booking_Group_ID: string;
  element_ID: string;
  sequence: number;
  booking_Exclude: number;
  element_Name: string;
  numeric: boolean;
  list: boolean;
  changeUser?: string | null;
  changeDateTime?: string | null;
}

interface BookingExcludedElementApiPayload {
  booking_ExcludedElement_ID: string;
  booking_Group_ID: string;
  element_ID: string;
  sequence: number;
  booking_Exclude: number;
  changeDateTime: string;
}

interface ExcludableElementApiModel {
  element_ID: string;
  element_Name: string;
  outputCode?: number | null;
  numeric: boolean;
  list: boolean;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class BookingExcludedElementService {
  private excludedElementUrl =
    environment.apiUrl + '/kbj-frontend/api/v1/BookingGroup/BookingExcludedElement';
  private elementUrl = environment.apiUrl + '/kbj-frontend/api/v1/Element';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all excluded elements ("Niet signaleren") for the given booking group.
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getExcludedElementsByGroup(bookingGroupId: string): Observable<BookingGroupExcludedElement[]> {
    return this.http
      .get<BookingExcludedElementApiModel[] | null>(
        this.excludedElementUrl + '/ByContext/' + bookingGroupId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiPayloadToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns the elements ("Uitvoercode") that can be excluded from a booking group.
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getExcludableElements(): Observable<Element[]> {
    return this.http
      .get<ExcludableElementApiModel[] | null>(
        this.elementUrl + '/ExcludedBookingGroupElements',
        this.httpOptions,
      )
      .pipe(
        map((jsonData) =>
          (jsonData ?? []).map((item) => ({
            elementId: item.element_ID,
            elementName: item.element_Name,
            outputCode: item.outputCode,
            numeric: item.numeric,
            list: item.list,
          })),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new excluded element. The id is forced to the empty guid and the
   * sequence to -1 so the backend can generate them.
   */
  addExcludedElement(excludedElement: BookingGroupExcludedElement): Observable<unknown> {
    const payload = this.mapModelToApiPayload({
      ...excludedElement,
      bookingGroupExcludedElementId: EMPTY_GUID,
      sequence: -1,
    });

    return this.http
      .post<unknown>(this.excludedElementUrl, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Updates an existing excluded element.
   */
  updateExcludedElement(
    id: string,
    excludedElement: BookingGroupExcludedElement,
  ): Observable<unknown> {
    const payload = this.mapModelToApiPayload({
      ...excludedElement,
      bookingGroupExcludedElementId: id,
    });

    return this.http
      .put<unknown>(this.excludedElementUrl + '/' + id, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Removes an excluded element by its id.
   */
  removeExcludedElement(id: string): Observable<unknown> {
    return this.http
      .delete(this.excludedElementUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  // ------------------------------------------------------------------------------
  // Helper methods for mapping between API payloads and the model.

  private mapApiPayloadToModel(
    apiModel: BookingExcludedElementApiModel,
  ): BookingGroupExcludedElement {
    return {
      bookingGroupExcludedElementId: apiModel.booking_ExcludedElement_ID,
      bookingGroupId: apiModel.booking_Group_ID,
      elementId: apiModel.element_ID,
      elementName: apiModel.element_Name,
      sequence: apiModel.sequence,
      bookingExclude: apiModel.booking_Exclude,
      numeric: apiModel.numeric,
      list: apiModel.list,
    };
  }

  private mapModelToApiPayload(
    model: BookingGroupExcludedElement,
  ): BookingExcludedElementApiPayload {
    return {
      booking_ExcludedElement_ID: model.bookingGroupExcludedElementId ?? EMPTY_GUID,
      booking_Group_ID: model.bookingGroupId,
      element_ID: model.elementId,
      sequence: model.sequence ?? -1,
      booking_Exclude: model.bookingExclude ?? 2,
      changeDateTime: new Date().toISOString(),
    };
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
